import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Download, Upload, PenLine, ShieldCheck, ArrowRight,
  CheckCircle2, AlertCircle, Loader2, Mail, Clock,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import SignaturePad from "../components/SignaturePad";
import { MailDeliveryHint } from "../components/MailDeliveryHint";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "alphalane_contract_token";

const fmtAmount = (n) => {
  const num = parseInt(String(n).replace(/[^0-9]/g, ""), 10);
  if (!num) return "";
  return num.toLocaleString("de-CH").replace(/\u202F/g, "'");
};

const todayStr = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
};

export default function ContractSign() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token") || "";
  const navigate = useNavigate();
  const [token, setToken] = useState(() => tokenFromUrl || (typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : "") || "");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("digital"); // 'digital' | 'upload'
  const [fullName, setFullName] = useState("");
  const [ort, setOrt] = useState("");
  const [amount, setAmount] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sigRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { document.title = "Dienstleistungsvertrag · Alphalane"; }, []);

  // Persist token from URL into localStorage and clean URL
  useEffect(() => {
    if (tokenFromUrl) {
      try {
        localStorage.setItem(TOKEN_KEY, tokenFromUrl);
      } catch (err) {
        log.warn("[contract] token persist failed:", err);
      }
      navigate("/onboarding/contract", { replace: true });
    }
  }, [tokenFromUrl, navigate]);

  // Fetch current contract status. Authentication can come either via the
  // localStorage Bearer token (pending pre-approval users) OR via the
  // standard auth cookie (already-logged-in approved users) — the backend
  // dependency `get_customer_for_contract` accepts both.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API}/contract/me`, {
          headers,
          withCredentials: true,
        });
        if (cancelled) return;
        setMe(data);
        if (data.first_name && !fullName) {
          setFullName(`${data.first_name} ${data.last_name || ""}`.trim());
        }
        // Stamp first-visit timestamp on the backend so the reminder job can
        // pick this customer up if they wander off without finishing.
        if (!data.signed_at) {
          axios.post(`${API}/contract/visit`, {}, {
            headers, withCredentials: true,
          }).catch(() => {/* best-effort, non-blocking */});
        }
      } catch (e) {
        if (cancelled) return;
        // Only block the page when we genuinely have no usable auth context.
        // The Bearer token may have expired *and* there's no session cookie —
        // in that case, drop to the "Zugang erforderlich" screen.
        if (e?.response?.status === 401) {
          setErr("Token ungültig oder abgelaufen. Bitte erneut einloggen.");
          try {
            localStorage.removeItem(TOKEN_KEY);
          } catch (lsErr) {
            log.warn("[contract] token clear failed:", lsErr);
          }
        } else {
          setErr(typeof e?.response?.data?.detail === "string"
            ? e.response.data.detail
            : "Konnte den Vertrags-Status nicht laden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitDigital = async (e) => {
    e.preventDefault();
    setErr("");
    if (!fullName.trim() || !ort.trim() || !amount.trim()) {
      setErr("Bitte alle Felder ausfüllen."); return;
    }
    if (!consent) { setErr("Bitte den Vertragsbedingungen zustimmen."); return; }
    const sig = sigRef.current?.toPNG?.();
    if (!sig) { setErr("Bitte deine Unterschrift setzen."); return; }
    const num = parseInt(String(amount).replace(/[^0-9]/g, ""), 10);
    if (!num || num < 100) { setErr("Startkapital muss mindestens 100 USD betragen."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/contract/sign`, {
        full_name: fullName.trim(),
        ort: ort.trim(),
        startbetrag: num,
        signature1_png: sig,
        signature2_png: sig,
        consent: true,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      toast.success("Vertrag erfolgreich unterschrieben!");
      setMe({ ...(me || {}), signed_at: data.signed_at, method: "digital", has_pdf: true, startbetrag: num, ort: ort.trim() });
    } catch (e2) {
      const det = e2?.response?.data?.detail;
      setErr(typeof det === "string" ? det : "Vertrag konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    setErr("");
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr("Bitte die unterschriebene PDF auswählen."); return; }
    if (!fullName.trim() || !ort.trim() || !amount.trim()) { setErr("Bitte alle Felder ausfüllen."); return; }
    if (!consent) { setErr("Bitte den Vertragsbedingungen zustimmen."); return; }
    const num = parseInt(String(amount).replace(/[^0-9]/g, ""), 10);
    if (!num || num < 100) { setErr("Startkapital muss mindestens 100 USD betragen."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("full_name", fullName.trim());
    fd.append("ort", ort.trim());
    fd.append("startbetrag", String(num));
    fd.append("consent", "true");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/contract/upload-signed`, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });
      toast.success("Vertrag erfolgreich hochgeladen!");
      setMe({ ...(me || {}), signed_at: data.signed_at, method: "upload", has_pdf: true, startbetrag: num, ort: ort.trim() });
    } catch (e2) {
      const det = e2?.response?.data?.detail;
      setErr(typeof det === "string" ? det : "Upload fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const onAmountChange = useCallback((v) => {
    setAmount(fmtAmount(v));
  }, []);

  if (loading) {
    return <PageShell><div className="al-card p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div></PageShell>;
  }

  // Render "Zugang erforderlich" ONLY when we have no usable auth context:
  // - no Bearer token in state AND
  // - no `me` payload loaded (which would prove the cookie session works)
  // - OR an explicit 401 came back
  if ((!token && !me) || err === "Token ungültig oder abgelaufen. Bitte erneut einloggen.") {
    return (
      <PageShell>
        <div className="al-card p-8 sm:p-10 text-center" data-testid="contract-no-token">
          <AlertCircle className="h-10 w-10 mx-auto text-amber-500" />
          <h1 className="mt-4 font-display font-bold text-2xl sm:text-3xl">Zugang erforderlich</h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
            Diese Seite ist nur über den Link aus der Bestätigungs-Mail oder den Login erreichbar.
          </p>
          <Link to="/login" className="al-cta mt-6 inline-flex" data-testid="contract-go-login">
            Zum Login <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageShell>
    );
  }

  if (me?.signed_at) {
    return (
      <PageShell>
        <div className="al-card p-8 sm:p-10 text-center" data-testid="contract-done">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-200">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-display font-bold text-2xl sm:text-3xl">Vertrag eingegangen!</h1>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
            Dein unterschriebener Dienstleistungsvertrag ist bei uns angekommen. Wir prüfen ihn
            zusammen mit deiner Registrierung und schalten dich anschließend für das Onboarding frei.
          </p>
          <div className="mt-6 text-sm text-slate-500 flex items-center justify-center gap-2">
            <Clock className="h-4 w-4" /> Bearbeitung: in der Regel innerhalb von 1–2 Werktagen
          </div>
          <MailDeliveryHint testid="contract-done-mail-hint" />
          <a
            href={`${API}/contract/pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="contract-done-download"
            onClick={(e) => {
              e.preventDefault();
              // Mobile browsers (especially Safari iOS) block blob: URLs in
              // popup windows. Strategy:
              //   - Desktop: open new tab with blob: URL.
              //   - Mobile: download the file directly via <a download>.
              // We sniff via a user-agent test rather than navigator.share —
              // sniff is reliable for this binary decision.
              const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
              const popup = isMobile ? null : window.open("about:blank", "_blank");
              fetch(`${API}/contract/pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "include",
              })
                .then(r => {
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  return r.blob();
                })
                .then(b => {
                  const url = URL.createObjectURL(b);
                  if (popup && !popup.closed) {
                    popup.location.href = url;
                  } else {
                    // Force-download path — works on iOS Safari too.
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Alphalane-Dienstleistungsvertrag-signiert.pdf";
                    a.rel = "noopener";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                })
                .catch((err) => {
                  if (popup && !popup.closed) popup.close();
                  log.error("[contract] PDF download failed:", err);
                  toast.error("Vertrag konnte nicht geöffnet werden. Bitte versuche es nochmal.");
                });
            }}
            className="al-cta mt-7 inline-flex justify-center"
          >
            Unterschriebenen Vertrag öffnen <Download className="h-4 w-4" />
          </a>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="al-card p-7 sm:p-10" data-testid="contract-page">
        <div className="flex items-start gap-4 mb-6">
          <div className="h-12 w-12 rounded-2xl al-grad-bg text-white flex items-center justify-center shrink-0">
            <PenLine className="h-6 w-6" />
          </div>
          <div>
            <span className="al-tag" style={{display:"inline-flex"}}>Schritt 1 von 3</span>
            <h1 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight">Dienstleistungsvertrag</h1>
            <p className="mt-2 text-slate-600 leading-relaxed">
              Den Vertrag kannst du <strong>direkt digital unterschreiben</strong> — oder als PDF herunterladen, ausdrucken und unterschrieben wieder hochladen. Wähle einfach den Weg, der für dich am bequemsten ist.
            </p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="mb-6 inline-flex rounded-xl border border-[var(--al-line)] bg-slate-50 p-1" data-testid="contract-tabs">
          <button
            type="button"
            onClick={() => setTab("digital")}
            data-testid="contract-tab-digital"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === "digital" ? "bg-white shadow-sm text-[var(--al-purple)]" : "text-slate-500 hover:text-slate-700"}`}
          >
            <PenLine className="inline h-3.5 w-3.5 mr-1.5" /> Digital unterschreiben
          </button>
          <button
            type="button"
            onClick={() => setTab("upload")}
            data-testid="contract-tab-upload"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === "upload" ? "bg-white shadow-sm text-[var(--al-purple)]" : "text-slate-500 hover:text-slate-700"}`}
          >
            <Upload className="inline h-3.5 w-3.5 mr-1.5" /> PDF hochladen
          </button>
        </div>

        {/* Read-only contract preview */}
        <div className="rounded-2xl border border-[var(--al-line)] bg-slate-50/60 p-4 sm:p-5 mb-6" data-testid="contract-preview">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white border border-[var(--al-line)] flex items-center justify-center">
                <FileText className="h-5 w-5 text-[var(--al-purple)]" />
              </div>
              <div>
                <div className="font-display font-semibold">Alphalane Dienstleistungsvertrag</div>
                <div className="text-xs text-slate-500">7 Seiten · digital signierbar oder zum Ausdrucken</div>
              </div>
            </div>
            <a
              href={`${API}/contract/template.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="contract-download-template"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--al-purple)] hover:underline"
            >
              <Download className="h-4 w-4" /> Vertrag herunterladen
            </a>
          </div>
        </div>

        {err && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="contract-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}

        {tab === "digital" ? (
          <form onSubmit={submitDigital} className="space-y-5" data-testid="contract-form-digital">
            <SharedFields
              fullName={fullName} setFullName={setFullName}
              ort={ort} setOrt={setOrt}
              amount={amount} onAmountChange={onAmountChange}
            />

            <div className="pt-2">
              <SignaturePad
                ref={sigRef}
                label="Deine Unterschrift (wird auf beide Unterschrift-Felder gesetzt)"
                testid="contract-signature"
                height={200}
              />
              <p className="mt-2 text-[11px] text-slate-500">
                Hinweis: Der Vertrag enthält zwei Unterschriftsfelder. Da beide Unterschriften identisch sein müssen, reicht es, hier einmal zu unterschreiben — wir setzen sie automatisch auf beide Felder.
              </p>
            </div>

            <ConsentBox value={consent} onChange={setConsent} />

            <button
              type="submit"
              disabled={busy}
              data-testid="contract-submit-digital"
              className="al-cta w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Vertrag wird signiert…</> : <>Vertrag jetzt unterschreiben <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={submitUpload} className="space-y-5" data-testid="contract-form-upload">
            <ol className="space-y-2 mb-2 text-sm text-slate-600">
              <li><strong className="text-slate-800">1.</strong> Vertrag oben mit <em>"Vertrag herunterladen"</em> öffnen, digital unterschreiben oder ausdrucken & von Hand unterschreiben (2× Unterschrift, Startkapital eintragen).</li>
              <li><strong className="text-slate-800">2.</strong> Eingescannen/abfotografieren und unterschriebene PDF unten hochladen.</li>
            </ol>

            <SharedFields
              fullName={fullName} setFullName={setFullName}
              ort={ort} setOrt={setOrt}
              amount={amount} onAmountChange={onAmountChange}
            />

            <label className="block">
              <span className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-block">Unterschriebene PDF hochladen *</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                ref={fileRef}
                required
                data-testid="contract-upload-input"
                className="block w-full text-sm text-slate-700 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:font-semibold file:bg-[var(--al-purple)]/10 file:text-[var(--al-purple)] hover:file:bg-[var(--al-purple)]/15"
              />
              <span className="text-xs text-slate-500 mt-1.5 inline-block">PDF, max. 20 MB. Bitte beide Unterschriften gut lesbar.</span>
            </label>

            <ConsentBox value={consent} onChange={setConsent} />

            <button
              type="submit"
              disabled={busy}
              data-testid="contract-submit-upload"
              className="al-cta w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird hochgeladen…</> : <>PDF hochladen <Upload className="h-4 w-4" /></>}
            </button>
          </form>
        )}

        <div className="mt-7 border-t border-[var(--al-line)] pt-5 flex items-center gap-3 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
          <span>Verschlüsselte Übertragung · Audit-Trail mit Zeitstempel, IP & Hash · DSGVO-konform.</span>
        </div>
      </div>
    </PageShell>
  );
}

/* --------- Subcomponents --------- */

function SharedFields({ fullName, setFullName, ort, setOrt, amount, onAmountChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Vollständiger Name *">
        <input
          type="text"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          data-testid="contract-fullname"
          className="al-input"
          placeholder="Vor- und Nachname"
        />
      </Field>
      <Field label="Ort (Stadt) *">
        <input
          type="text"
          required
          value={ort}
          onChange={(e) => setOrt(e.target.value)}
          data-testid="contract-ort"
          className="al-input"
          placeholder="z. B. Berlin"
        />
      </Field>
      <Field label="Startkapital in USD *">
        <input
          type="text"
          required
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          data-testid="contract-amount"
          className="al-input"
          placeholder="z. B. 10'000"
          inputMode="numeric"
        />
      </Field>
      <Field label="Datum (automatisch)">
        <input
          type="text"
          value={todayStr()}
          readOnly
          data-testid="contract-date"
          className="al-input bg-slate-50 text-slate-500"
        />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 inline-block">{label}</span>
      {children}
    </label>
  );
}

function ConsentBox({ value, onChange }) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-[var(--al-line)] bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100/60 transition">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="contract-consent"
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[var(--al-purple)] focus:ring-[var(--al-purple)]"
      />
      <span className="text-sm text-slate-700 leading-relaxed">
        Ich habe den <a href={`${API}/contract/template.pdf`} target="_blank" rel="noreferrer" className="text-[var(--al-purple)] hover:underline">Dienstleistungsvertrag</a> inklusive aller Risikohinweise gelesen
        und stimme den darin enthaltenen Bedingungen <strong>verbindlich</strong> zu. Mir ist bewusst,
        dass Trading mit Verlustrisiken verbunden ist und keine Erfolgsgarantie besteht.
      </span>
    </label>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="contract-shell">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]"><span className="al-grad-text">ALPHALANE</span></div>
          </Link>
          <a href="mailto:kontakt@moonshine-solutions.com" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Support
          </a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-14">{children}</main>
    </div>
  );
}
